<script setup lang="ts">
import debounce from 'lodash.debounce';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { ComponentExposed } from 'vue-component-type-helpers';
import { type Location, type SearchMode } from '@utils/types';
import { type LocationGroup } from '@search/types';
import { MrSelect } from '#components';
import { type SelectItemType } from '#imports';
import { type ReserveType } from '@utils/itinerary';

interface IProps {
    options?: LocationGroup[];
    placeholder?: string;
    disabled?: boolean;
    debounceSearch?: boolean;
    icon?: IconDefinition;
    emptyListMessage?: string;
    showGroupTitles?: boolean;
    logAnalyseServiceType?: string;
    reserveType: ReserveType;
    searchMode: SearchMode;
}

const {
    options = [],
    placeholder = 'انتخاب کنید',
    disabled,
    emptyListMessage = 'نتیجه‌ای پیدا نشد.',
    showGroupTitles,
    logAnalyseServiceType = '',
    debounceSearch,
    searchMode,
    reserveType,
} = defineProps<IProps>();

const emit = defineEmits<{
    done: [];
    open: [];
    close: [];
    filled: [];
    emptied: [value: string];
    search: [token: string];
    btnClose: [];
}>();
const modelValue = defineModel<Location | null>({ default: null });
const loading = defineModel<boolean>('loading');
const analyticsStore = useAnalyticsStore();

const select = ref<ComponentExposed<typeof MrSelect>>();
const { addEventListener, emitListener } = addListener();
const focusToNext = ref(true);
const isSwiped = ref(false);
const analyseTypedStrings = ref<string[]>([]);
const router = useRouter();
const label = computed(() => `انتخاب ${placeholder}`);
const localOptions = computed(() => mapLocationsToOptions(options));
const localValue = computed({
    get(): SelectItemType | undefined {
        let option: SelectItemType | undefined;
        localOptions.value.forEach(o => {
            if (!option) {
                option = o.subItems?.find(i => i.id === modelValue.value?.code);
            }
            if (!option) {
                option = o.subItems
                    ?.flatMap(si => si.subItems)
                    .filter(Boolean)
                    .find(i => i?.id === modelValue.value?.code);
            }
        });
        return option;
    },
    set(val: SelectItemType | undefined) {
        if (val) {
            modelValue.value = val.extraValue;
        } else {
            throw new Error('value not handled');
        }
    },
});
const debouncedSearch = computed(() =>
    debounce((token: string) => {
        emit('search', token);
    }, 700)
);
const toggleFocusToNext = () => {
    focusToNext.value = false;
    nextTick(() => (focusToNext.value = true));
};
const hide = () => select.value?.close();
const closeButtonClicked = () => {
    onBtnClose();
    hide();
};
const onSearch = (search: string) => {
    if (logAnalyseServiceType) {
        analyseTypedStrings.value.push(search);
    }
    if (debounceSearch) {
        loading.value = !!search;
        debouncedSearch.value(search);
    } else {
        emit('search', search);
    }
};
const focus = () => {
    select.value?.focus();
    return true;
};
const onDone = () => {
    emit('done');
    emitListener('done');
};
const onOpen = () => {
    emit('open');
    emitListener('open');
};

function backButtonClicked() {
    analyticsStore.trackPosthog('search-form-back', {
        reserveType,
        searchMode,
        inputComponent: 'city-select',
    });
    router.back();
}
const onBtnClose = () => {
    analyticsStore.trackPosthog('search-form-close', {
        reserveType,
        searchMode,
        inputComponent: 'city-select',
    });
    emit('btnClose');
    emitListener('btn-close');
};
const mapLocationsToOptions = (list: LocationGroup[]) =>
    list.map<SelectItemType>(val => ({
        id: val.title || '',
        label: val.title || '',
        isGroup: true,
        ignoreSearch: val.ignoreGroupInSearch,
        subItems: val.locations.map(loc => ({
            id: loc.code,
            label: loc.title || '',
            subLabel: loc.countryName,
            subTitle: loc.subtitle,
            icon: loc.icon || val.icon,
            ignoreSearch: val.ignoreGroupInSearch,
            extraValue: loc,
            subItems: loc.subLocations?.map(l => ({
                id: l.code,
                label: l.title || '',
                subLabel: l.countryName,
                subTitle: l.subtitle,
                icon: l.icon || val.icon,
                isSubItem: true,
                ignoreSearch: val.ignoreGroupInSearch,
                extraValue: l,
            })),
        })),
    }));
watch([modelValue, localOptions], () => {
    toggleFocusToNext();
});
watch(localValue, (val, oldVal) => {
    if (
        val &&
        val.label !== oldVal?.label &&
        logAnalyseServiceType &&
        analyseTypedStrings.value.length
    ) {
        analyticsStore.sendUserLog({
            searchLogRequest: {
                acceptString: val.label,
                serviceType: logAnalyseServiceType,
                typedStrings: analyseTypedStrings.value,
            },
        });
        analyseTypedStrings.value = [];
    }
});

defineExpose({
    focus,
    isSwiped,
    addEventListener,
    hide,
});
</script>

<template>
    <div class="city-select-container">
        <mr-select
            ref="select"
            v-model="localValue"
            return-object
            :icon="icon || undefined"
            :options="localOptions"
            :placeholder
            :label
            :disabled
            :loading
            :empty-list-message
            :min-width="350"
            :show-groups-in-search="showGroupTitles"
            disable-local-search
            hide-open-indicator
            hide-description
            mobile-modal
            hide-label
            paging-modal-animation
            required
            @search="onSearch"
            @select-option="onDone"
            @open="onOpen"
            @close="emit('close')"
            @emptied="emit('emptied', $event)"
            @filled="emit('filled')"
            @close-button="closeButtonClicked"
            @back-clicked="backButtonClicked"
        />
    </div>
</template>

<style lang="scss" scoped>
.city-select-container {
    position: relative;
}

::-webkit-search-cancel-button {
    display: none;
}
</style>
